import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Phone, Mail, MessageCircle, Bot, Zap, Shield, Globe, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const products = [
    {
      icon: MessageSquare,
      title: 'SMS',
      description: 'Reliable bulk SMS delivery with high throughput and global reach. Perfect for OTPs, alerts, and marketing campaigns.',
      image: 'https://images.pexels.com/photos/3943951/pexels-photo-3943951.jpeg?w=400'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp Business',
      description: 'Official WhatsApp Business API for rich, interactive customer conversations at scale with verified business accounts.',
      image: 'https://images.pexels.com/photos/46924/pexels-photo-46924.jpeg?w=400'
    },
    {
      icon: Phone,
      title: 'Voice',
      description: 'Crystal-clear voice calls and IVR solutions for customer engagement, automated calling, and verification services.',
      image: 'https://images.pexels.com/photos/5206291/pexels-photo-5206291.jpeg?w=400'
    },
    {
      icon: Zap,
      title: 'RCS',
      description: 'Next-generation Rich Communication Services with interactive cards, carousels, and rich media messaging capabilities.',
      image: 'https://images.pexels.com/photos/607812/pexels-photo-607812.jpeg?w=400'
    },
    {
      icon: Mail,
      title: 'Email',
      description: 'Enterprise-grade email delivery with high deliverability rates, analytics, and template management for transactional and marketing emails.',
      image: 'https://images.pexels.com/photos/840996/pexels-photo-840996.jpeg?w=400'
    },
    {
      icon: Bot,
      title: 'Bots',
      description: 'Intelligent Voice & WhatsApp bots powered by AI for automated customer support, lead generation, and conversational commerce.',
      image: 'https://images.pexels.com/photos/18069697/pexels-photo-18069697.png?w=400'
    }
  ];

  const industries = [
    {
      name: 'Banking & Finance',
      description: 'Secure OTP delivery, transaction alerts, and customer notifications with 99.9% uptime guarantee.',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400'
    },
    {
      name: 'NBFC',
      description: 'Automated loan reminders, payment confirmations, and personalized financial advisory through multi-channel messaging.',
      image: 'https://images.pexels.com/photos/50987/money-card-business-credit-card-50987.jpeg?w=400'
    },
    {
      name: 'Retail & E-commerce',
      description: 'Order updates, delivery tracking, promotional campaigns, and customer engagement across all touchpoints.',
      image: 'https://images.unsplash.com/photo-1522684462852-01b24e76b77d?w=400'
    },
    {
      name: 'D2C Brands',
      description: 'Direct customer communication for product launches, abandoned cart recovery, and personalized marketing.',
      image: 'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?w=400'
    },
    {
      name: 'EdTech',
      description: 'Class reminders, exam notifications, parent-teacher communication, and student engagement solutions.',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400'
    },
    {
      name: 'Healthcare',
      description: 'Appointment reminders, prescription updates, telemedicine support, and emergency notifications.',
      image: 'https://images.unsplash.com/photo-1603354350317-6f7aaa5911c5?w=400'
    }
  ];

  const features = [
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Connect with customers across 190+ countries'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-grade encryption and compliance certifications'
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Sub-second delivery with 99.9% uptime SLA'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background with overlay */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1629422372786-1cc7e9282ac2?w=1920)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/95 via-slate-900/90 to-slate-800/95" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-cyan-500/10 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="space-y-8 animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
              Power Your Business with
              <span className="block mt-2 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
                Next-Gen Communication
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
              Enterprise CPaaS platform delivering SMS, WhatsApp, Voice, RCS, Email, and AI-powered Bots across global networks
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
              <Link to="/products">
                <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105">
                  Explore Products
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg rounded-xl transition-all duration-300 hover:scale-105">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Floating elements */}
        <div className="absolute bottom-10 left-10 w-20 h-20 bg-cyan-500/20 rounded-full blur-2xl animate-pulse" />
        <div className="absolute top-20 right-20 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl mb-6 shadow-lg">
                    <feature.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our Communication Solutions
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Comprehensive suite of APIs and tools to power your customer engagement
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <Card key={index} className="group overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-white rounded-xl shadow-lg">
                      <product.icon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">{product.title}</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">{product.description}</p>
                  <Link to="/products" className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors">
                    Learn More
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/products">
              <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                View All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Tailored communication solutions for diverse business sectors
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <Card key={index} className="group overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-500">
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={industry.image} 
                    alt={industry.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent" />
                  <div className="absolute inset-0 flex items-end p-6">
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2">{industry.name}</h3>
                      <p className="text-slate-200 leading-relaxed">{industry.description}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-slate-900 to-slate-800" />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1521106047354-5a5b85e819ee?w=1920')] bg-cover bg-center opacity-10" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Communication?
          </h2>
          <p className="text-xl text-slate-200 mb-10 leading-relaxed">
            Join thousands of businesses leveraging Smartbeeps for reliable, scalable communication solutions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/contact">
              <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white px-10 py-7 text-lg rounded-xl shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105">
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="mailto:sales@smartbeeps.com">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-10 py-7 text-lg rounded-xl transition-all duration-300">
                Talk to Sales
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;